import padStart from 'lodash/padStart';
import { intl, number } from '@frameio/i18n';
import { Timecode } from '@frameio/vapor';
import { getBrowserSafeSeconds } from '../formatters';
import TEXT from './generated/TEXT/media';

TEXT.DEFINE({
  /** Abbreviated page label, e.g. "pg. 02" */
  pageAbbr: (page: string): string => `pg. ${page}`,
  /** Full page label, e.g. "Page 2" */
  pageFull: (page: number): string => `Page ${number(page)}`,
});

type GetOriginalTimeOpts = {
  currentTime: number;
};

type GetTimecodeObjectOpts = {
  currentTime: number;
  startTimecode: string | null;
  fps: number;
  frames: number;
};

export const DEFAULT_TIMECODE = '00:00:00:00';

export function isDroppingFrames(timecode: string | null): boolean {
  // Timecode using dropped frames contains a semicolon
  if (timecode) {
    return /;/.test(timecode);
  }
  return false;
}

/**
 * When calculating a new `currentTime` for a frame number like this:
 *
 * ```js
 * $('video').currentTime = frameNumber * (1 / fps)
 * ```
 *
 * The resulting time will land at the left edge of each frame like this:
 *
 * 0  1  2  3  …
 * ┌──┬──┬──┬──┬
 * │  │  │  │  │
 * └──┴──┴──┴──┴
 *
 * By adding 1/3 of a frame, we land squarely inside the frame while avoiding
 * potential rounding errors from going over more than half a frame.
 */
export const getFrameOffset = (frameRate: number): number => {
  return 1 / frameRate / 3;
};

/**
 * Uses the frame rate to calculate the proper difference in seconds to jump by.
 *
 * This is useful for when we want to increment or decrement by a number of frames,
 * but our starting point is not a whole number of frames.
 *
 * @example
 * ```ts
 * // Increment by 1 frame
 * jumpTimeByFrames({ initialTime: 69.831344, diffFrames: 1, frameRate: 29.97 })
 * // 69.86987 seconds
 *
 * // Decrement by 1 frame
 * jumpTimeByFrames({ initialTime: 69.831344, diffFrames: -1, frameRate: 29.97 })
 * // 69.800311 seconds
 * ```
 */
export function jumpTimeByFrames({
  initialTime,
  diffFrames,
  frameRate,
}: {
  /**
   * The initial time in seconds
   */
  initialTime: number;
  /**
   * The difference in frames to add to the initial time
   * +1 to increment, -1 to decrement
   */
  diffFrames: number;
  /**
   * The frame rate of the media
   */
  frameRate: number;
}): number {
  const frameCount = initialTime * frameRate;
  const newFrameCount = Math.floor(frameCount + diffFrames);

  return getBrowserSafeSeconds(
    newFrameCount / frameRate + getFrameOffset(frameRate)
  )!;
}

/**
 * Computes the next playback time when stepping by frames.
 *
 * By default this matches the existing video/audio behavior and simply moves
 * forward or backward by `diffFrames` from the current time.
 *
 * When `wrapAround` is enabled, the step is treated as circular over the
 * provided `totalFrames`. This is used by model assets so arrow-key rotation
 * can continue seamlessly from the last frame back to the first frame, and
 * vice versa.
 */
export function getFrameStepTime({
  currentTime,
  diffFrames,
  frameRate,
  totalFrames,
  wrapAround = false,
}: {
  currentTime: number;
  diffFrames: number;
  frameRate: number;
  totalFrames?: number;
  wrapAround?: boolean;
}): number {
  if (!wrapAround || !totalFrames) {
    return jumpTimeByFrames({
      initialTime: currentTime,
      diffFrames,
      frameRate,
    });
  }

  const currentFrame = timeToWrappedFrameIndex({
    currentTime,
    fps: frameRate,
    totalFrames,
  });
  const wrappedFrame = wrapFrameIndex(currentFrame + diffFrames, totalFrames);

  return jumpTimeByFrames({
    initialTime: 0,
    diffFrames: wrappedFrame,
    frameRate,
  });
}

export function convertSecondsToMicroseconds(
  seconds?: number | null
): number | null {
  return seconds || seconds === 0 ? Math.round(seconds * 1000000) : null;
}

const FULL_ROTATION_DEGREES = 360;

/**
 * Wraps a frame index into the range `[0, totalFrames)`.
 *
 * Handles both overflow (frame >= totalFrames) and negative indices,
 * making it safe to use for forward and backward frame stepping.
 * Returns 0 if `totalFrames` is invalid.
 */
export function wrapFrameIndex(frame: number, totalFrames: number): number {
  if (!totalFrames || totalFrames < 1) {
    return 0;
  }

  return ((frame % totalFrames) + totalFrames) % totalFrames;
}

/**
 * HACK ALERT
 * Tolerance applied in *frame units* (after multiplying time by fps)
 * to absorb cumulative precision loss between writing currentTime to
 * the HTMLMediaElement and reading it back.
 *
 * Without this, `Math.floor((N/fps) * fps)` returns `N - 1` for any
 * fps where `N/fps` is a non-terminating decimal (6, 23.976, 29.97,
 * 50.94, 59.94 — and most non-multiples-of-5). Sources of loss:
 *
 *   1. `getBrowserSafeSeconds` rounds the committed time to 6
 *      decimals → up to 5e-7 s error.
 *   2. Browsers further truncate the element's stored currentTime —
 *      observed in Chrome: `el.currentTime = 0.666666` after writing
 *      `0.666667`, i.e. another ~1e-6 s gap.
 *
 * Combined worst case is ~1.5e-6 s, which in frame units scales with
 * fps (at 240 fps that is ~3.6e-4 frames). 1e-3 frames (0.1% of a
 * frame) comfortably covers all realistic fps. A position genuinely
 * within 0.1% of the next frame boundary is closer than 1 µs at most
 * fps and is indistinguishable from the boundary under the upstream
 * rounding anyway, so no observable leakage.
 *
 * FLAG: this is safe up to ~667 fps; revisit if we start ingesting
 * high-speed footage (should update to Math.max(1e-3, fps * 3e-6) if so)
 */
const FRAME_FLOOR_PRECISION_TOLERANCE = 1e-3;

/**
 * Converts a playback time to a frame index in the range `[0, totalFrames)`.
 *
 * Uses `Math.floor` semantics — a frame is active from its start time up to
 * (but not including) the next frame's start time. The result is wrapped via
 * {@link wrapFrameIndex} so it is always a valid index into the frame range.
 * Returns 0 if `fps` or `totalFrames` is invalid.
 */
export function timeToWrappedFrameIndex({
  currentTime,
  fps,
  totalFrames,
}: {
  currentTime: number;
  fps: number;
  totalFrames: number;
}): number {
  if (!totalFrames || totalFrames < 1 || !isFinite(fps) || fps <= 0) {
    return 0;
  }

  return wrapFrameIndex(
    Math.floor(currentTime * fps + FRAME_FLOOR_PRECISION_TOLERANCE),
    totalFrames
  );
}

/**
 * Maps a time position within a duration to 0–360° (e.g. for model turntable display).
 */
export function timeToDegrees(currentTime: number, duration: number): number {
  if (!isFinite(duration) || duration <= 0) return 0;
  const ratio = Math.min(1, Math.max(0, currentTime / duration));
  return Math.round(ratio * FULL_ROTATION_DEGREES) % FULL_ROTATION_DEGREES;
}

/**
 * Maps playback time to the turntable angle for the current discrete frame.
 * Each frame maps evenly around 0–360° (e.g. 20 frames → 0°, 18°, 36°, …).
 *
 * Uses the same frame index as the model turntable dial:
 * `floor(currentTime * fps) % totalFrames`.
 *
 * When `totalFrames` or `fps` is missing or invalid, falls back to continuous
 * {@link timeToDegrees}.
 */
export function timeToFrameSnappedDegrees({
  currentTime,
  duration,
  totalFrames,
  fps,
}: {
  currentTime: number;
  duration: number;
  totalFrames: number;
  fps: number;
}): number {
  if (!totalFrames || totalFrames < 1 || !isFinite(fps) || fps <= 0) {
    return timeToDegrees(currentTime, duration);
  }

  const frameIndex = timeToWrappedFrameIndex({
    currentTime,
    fps,
    totalFrames,
  });

  return Math.floor((frameIndex * FULL_ROTATION_DEGREES) / totalFrames);
}

type ConvertMicrosecondsToSecondsReturnType<T> = T extends number
  ? number
  : null;
export function convertMicrosecondsToSeconds<
  T extends number | null | undefined,
>(microseconds: T): ConvertMicrosecondsToSecondsReturnType<T> {
  return (
    typeof microseconds === 'number'
      ? getBrowserSafeSeconds(microseconds / 1000000)
      : null
  ) as ConvertMicrosecondsToSecondsReturnType<T>;
}

/**
 * If the gap between the reported number of frames vs the calculated number
 * of frames (from duration * fps) is larger than this number, we consider
 * the reported number of frames to be inaccurate, so we fall back to to using
 * the calculated number of frames instead.
 *
 * ref: https://github.com/Frameio/web-components/blob/master/src/utils/mediaUtils.js#L25-L50
 */
const MAX_FRAME_NUM_DIFFERENCE = 10;
export function isLikelyAVFRAsset(media: {
  duration: number;
  fps: number;
  frames: number;
  isVfr: boolean;
}): boolean {
  if (!media.duration || !media.fps || !media.frames) return false;

  const computedTotalFrames = Math.round(media.duration * media.fps);
  const frameDiff = Math.abs(computedTotalFrames - media.frames);
  return media.isVfr || frameDiff > MAX_FRAME_NUM_DIFFERENCE;
}

/**
 * {@link Timecode} stores rational fps (e.g. 30000/1001) as `_fpsNumerator` /
 * `_fpsDenominator`. They are not a public API; we read them from a zero
 * anchor so {@link getTimecodeForTime} matches the frame index implied by
 * `new Timecode(new Date(...), rate, drop)` while using **microseconds**, so
 * sub-millisecond offsets are not lost to `Date`’s millisecond resolution.
 *
 * If `@frameio/vapor` removes or renames these fields, update this helper and
 * fix the `getTimecodeForTime` tests in `media.spec.ts`.
 */
type RationalFpsProbe = {
  _fpsNumerator: number;
  _fpsDenominator: number;
};

/**
 * Same rational-FPS mapping as {@link Timecode}'s `Date` constructor
 * (`floor(elapsedMs * fpsNum / (1000 * fpsDen)))` but using **microseconds**
 * so values like 708334µs are not collapsed when JS `Date` truncates
 * fractional milliseconds from `time * 1000`.
 */
function frameCountFromElapsedSecondsForTimecode(
  elapsedSeconds: number,
  frameRate: number,
  dropFrame?: boolean
): number {
  const anchor = new Timecode('00:00:00:00', frameRate, dropFrame);
  const { _fpsNumerator, _fpsDenominator } =
    anchor as unknown as RationalFpsProbe;
  const us = Math.round(elapsedSeconds * 1_000_000);
  return Math.floor((us * _fpsNumerator) / (1_000_000 * _fpsDenominator));
}

/**
 * Get the timecode object associated with the time, frame rate and timecode of
 * the video.
 *
 * `time` is passed through {@link getBrowserSafeSeconds} (6 decimal places) so
 * browser float noise stays bounded while offsets like `708334 / 1e6` stay
 * distinguishable for frame math.
 */
export function getTimecodeForTime(
  timecode: string,
  frameRate: number,
  time: number
): Timecode {
  const safeTime = getBrowserSafeSeconds(time) ?? 0;
  const drop = isDroppingFrames(timecode);
  try {
    const frameCount = frameCountFromElapsedSecondsForTimecode(
      safeTime,
      frameRate,
      drop
    );
    return new Timecode(frameCount, frameRate, drop);
  } catch (error) {
    /**
     * BUGS-1048: When the timecode has a `;` but the frame rate is not actually
     * a drop-frame frame rate like 29.97 or 59.94, assume that the timecode is
     * incorrect and fallback to non drop-frame.
     */
    if ((error as any).message?.includes('Drop frame is only supported')) {
      const frameCount = frameCountFromElapsedSecondsForTimecode(
        safeTime,
        frameRate,
        false
      );
      return new Timecode(frameCount, frameRate, false);
    }

    // Rethrow all other errors
    throw error;
  }
}

export function getSecondsFromTimecode({
  timecode,
  frameRate,
  startTimecode,
}: {
  timecode: string;
  frameRate: number;
  startTimecode: string;
}): number {
  try {
    // Parse the target timecode
    const targetTimecode = new Timecode(
      timecode,
      frameRate,
      isDroppingFrames(timecode)
    );

    // Parse the start timecode to get the offset
    let startTc;
    try {
      startTc = new Timecode(
        startTimecode,
        frameRate,
        isDroppingFrames(startTimecode)
      );
    } catch (error) {
      startTc = new Timecode(DEFAULT_TIMECODE, frameRate);
    }

    // Calculate the difference in frame count
    const frameCount = targetTimecode.frameCount - startTc.frameCount;

    if (frameCount === 0) {
      return 0;
    }

    // Convert frame count to seconds and add frame offset to land in the middle of the frame
    const seconds =
      getBrowserSafeSeconds(
        frameCount / frameRate + getFrameOffset(frameRate)
      ) ?? 0;
    return seconds;
  } catch (error) {
    // Handle drop frame errors similar to getTimecodeForTime
    if ((error as any).message?.includes('Drop frame is only supported')) {
      try {
        // Retry without drop frame
        const targetTimecode = new Timecode(timecode, frameRate, false);
        const startTc = new Timecode(startTimecode, frameRate, false);
        const frameCount = targetTimecode.frameCount - startTc.frameCount;
        return getBrowserSafeSeconds(frameCount / frameRate) ?? 0;
      } catch (retryError) {
        // If still failing, return 0 as fallback
        return 0;
      }
    }

    // For other errors, return 0 as fallback
    return 0;
  }
}

/**
 * Get the timecode object associated with the clock time, frame rate and
 * starting timecode of the video. This handles the case when the starting
 * timecode does not begin at zero.
 *
 * Defaults to DEFAULT_TIMECODE if timecode is invalid.
 */
export function getOffsetTimecode(
  timecodeIn: string,
  frameRate: number,
  time: number
): Timecode {
  let timecode;
  try {
    timecode = getTimecodeForTime(timecodeIn, frameRate, time);
    timecode.add(timecodeIn);
  } catch (e) {
    timecode = getTimecodeForTime(DEFAULT_TIMECODE, frameRate, time);
    timecode.add(DEFAULT_TIMECODE);
  }

  return timecode;
}

/**
 * At the time, this will simply return the current time.
 */
export function getOriginalCurrentTime({
  currentTime,
}: GetOriginalTimeOpts): number {
  return currentTime;
}

/**
 * Takes current time in seconds and converts to standard player output.
 * @param {number} currentTime - The video's current time.
 * @returns {string} - Time in standard format for display. Eg. 00:01.
 */
export const timeToStandard = (
  currentTime: number,
  includesDecimal = false
): string => {
  /**
   * The values are floored so that the tick only happens exactly at the correct
   * boundary where the previous second/minute/hour is complete (when incrementing).
   */
  const seconds = Math.floor(currentTime % 60);
  const minutes = Math.floor((currentTime % (60 * 60)) / 60);
  const hours = Math.floor(currentTime / (60 * 60));
  let formattedTime = [
    ...(hours > 0 ? [padStart(String(hours), 2, '0')] : []),
    padStart(String(minutes), 2, '0'),
    padStart(String(seconds), 2, '0'),
  ].join(':');
  if (includesDecimal) {
    const fraction = (currentTime % 1).toFixed(2).replace('0.', '.');
    formattedTime += fraction;
  }
  return formattedTime;
};

export const getTimecodeObject = ({
  currentTime,
  startTimecode,
  fps,
  frames,
}: GetTimecodeObjectOpts) => {
  /**
   * Get the starting frame number, which needs to be added to the current
   * frame number.
   */
  const validatedTimecodeIn = startTimecode || DEFAULT_TIMECODE;
  let startTc;
  try {
    startTc = new Timecode(validatedTimecodeIn, fps);
  } catch (error) {
    startTc = new Timecode(DEFAULT_TIMECODE, fps);
  }

  const startFrame = startTc.frameCount;
  const timecode = getOffsetTimecode(validatedTimecodeIn, fps, currentTime);

  /**
   * Because the current frame number starts from 0, the max frame number that
   * can be reached is `computedTotalFrames - 1`.
   */
  const maxFrames = Math.floor(startFrame + frames - 1);

  if (maxFrames > 0 && timecode.frameCount > maxFrames) {
    timecode.subtract(timecode.frameCount - maxFrames);
  }
  return timecode;
};

/**
 * There cannot be a `00` page
 */
export const reIndexPage = (page: number): number => (page === 0 ? 1 : page);

/**
 * Formats a number as a DocumentAsset display string, for example `pg. 02`.
 */
export const getFormattedPageNumber = (
  page: number,
  shouldAbbr = true
): string => {
  const count = reIndexPage(page);
  const abbrCount = new Intl.NumberFormat(intl().locale, {
    minimumIntegerDigits: 2,
  }).format(count);

  if (!shouldAbbr) {
    return TEXT.pageFull(count);
  }
  return TEXT.pageAbbr(abbrCount);
};
